
import { useState, useEffect } from "react";
import SearchBar from "@/components/SearchBar";
import CategoryFilter from "@/components/CategoryFilter";
import SportsFilter from "@/components/SportsFilter";
import PlatformFilter from "@/components/PlatformFilter";
import ToolCard from "@/components/ToolCard";
import SubmitToolDialog from "@/components/SubmitToolDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import type { Tool } from "@/data/tools";
import usePageTitle from '../hooks/usePageTitle';

const AllTools = () => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedSportId, setSelectedSportId] = useState<string | null>(null);
  const [selectedPlatformId, setSelectedPlatformId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tools, setTools] = useState<Tool[]>([]);
  
  // Data for filters
  const [categories, setCategories] = useState<{id: string, name: string}[]>([]);
  const [sports, setSports] = useState<{id: string, name: string}[]>([]);
  const [platforms, setPlatforms] = useState<{id: string, name: string}[]>([]);

  // Set the page title
  usePageTitle('All Tools');

  useEffect(() => {
    loadFiltersData();
    loadTools();
  }, [selectedCategoryIds, selectedSportId, selectedPlatformId]);

  const loadFiltersData = async () => {
    try {
      // Load categories, platforms, and sports from the database
      const { data: categoriesData } = await supabase
        .from('categories')
        .select('id, name')
        .order('name');

      const { data: platformsData } = await supabase
        .from('platforms')
        .select('id, name')
        .order('name');

      const { data: sportsData } = await supabase
        .from('sports')
        .select('id, name')
        .order('name');

      if (categoriesData) setCategories(categoriesData);
      if (platformsData) setPlatforms(platformsData);
      if (sportsData) setSports(sportsData);
    } catch (error) {
      console.error('Error loading filter data:', error);
      toast({
        title: "Error loading filters",
        description: "There was a problem loading the filters. Please refresh the page.",
        variant: "destructive",
      });
    }
  };

  const loadTools = async () => {
    setIsLoading(true);
    
    try {
      // Base query to get apps that are approved
      let query = supabase
        .from('apps')
        .select(`
          id, 
          name, 
          description, 
          visit_url,
          url_slug,
          approved,
          featured,
          logo_url,
          app_categories!inner(category_id, categories(name)), 
          app_platforms!inner(platform_id, platforms(name)), 
          app_sports!inner(sport_id, sports(name))
        `)
        .eq('approved', true);
      
      // Apply category filters if selected
      if (selectedCategoryIds.length > 0) {
        // Use in() operator for multiple category ids
        query = query.in('app_categories.category_id', selectedCategoryIds);
      }
      
      // Apply other filters if selected
      if (selectedSportId) {
        query = query.eq('app_sports.sport_id', selectedSportId);
      }
      
      if (selectedPlatformId) {
        query = query.eq('app_platforms.platform_id', selectedPlatformId);
      }
      
      // Execute the query
      const { data, error } = await query;
      
      if (error) throw error;
      
      // Transform the data into the Tool format
      if (data) {
        const transformedTools: Tool[] = data.map(app => {
          // Extract unique categories, platforms, and sports
          const categories = Array.from(new Set(
            app.app_categories.map((c: any) => c.categories.name)
          ));
          
          const platforms = Array.from(new Set(
            app.app_platforms.map((p: any) => p.platforms.name)
          ));
          
          const sports = Array.from(new Set(
            app.app_sports.map((s: any) => s.sports.name)
          ));
          
          return {
            id: app.id,
            name: app.name,
            logo: app.logo_url || "https://placehold.co/60x60", // Use logo_url or default
            rating: 4.5, // Default rating
            reviews: 0, // Default reviews count
            bookmarks: 0, // Default bookmarks count
            pricing: "Free Trial", // Default pricing
            description: app.description,
            tags: [], // Could be derived from categories or sports
            category: categories[0] || "", // Using the first category
            sports: sports,
            platforms: platforms,
            featured: app.featured,
            visitUrl: app.visit_url,
            url_slug: app.url_slug, // Add the URL slug
          };
        });
        
        setTools(transformedTools);
      }
    } catch (error) {
      console.error('Error loading tools:', error);
      toast({
        title: "Error loading apps",
        description: "There was a problem loading the apps. Please refresh the page.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for toggling category selection
  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategoryIds(prev => {
      if (prev.includes(categoryId)) {
        // Remove the category if already selected
        return prev.filter(id => id !== categoryId);
      } else {
        // Add the category if not selected
        return [...prev, categoryId];
      }
    });
  };

  // Filter tools by search query
  const filteredTools = tools.filter((tool) => {
    const matchesSearch = !searchQuery || 
      tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (tool.tags && tool.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())));
    
    return matchesSearch;
  });

  const featuredTools = filteredTools.filter(tool => tool.featured);
  const regularTools = filteredTools.filter(tool => !tool.featured);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navigation />
      <div className="container py-8 flex-grow">
        <h1 className="text-4xl font-bold mb-2">All Fitness Apps</h1>
        <p className="text-gray-600 mb-8">Browse and filter all available fitness applications</p>
        
        {/* Show search bar first on mobile */}
        <div className="lg:hidden mb-6">
          <SearchBar value={searchQuery} onChange={setSearchQuery} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto space-y-6 pb-6">
              <div className="space-y-6">
                <div className="hidden lg:block">
                  <SearchBar value={searchQuery} onChange={setSearchQuery} />
                </div>
                <div className="lg:block">
                  <CategoryFilter
                    categories={categories}
                    selectedCategoryIds={selectedCategoryIds}
                    onSelectCategory={handleCategorySelect}
                  />
                </div>
                <div className="lg:block">
                  <SportsFilter
                    sports={sports}
                    selectedSportId={selectedSportId}
                    onSelectSport={setSelectedSportId}
                  />
                </div>
                <div className="lg:block">
                  <PlatformFilter
                    platforms={platforms}
                    selectedPlatformId={selectedPlatformId}
                    onSelectPlatform={setSelectedPlatformId}
                  />
                </div>
                <SubmitToolDialog />
              </div>
            </div>
          </aside>
          
          <main className="lg:col-span-3">
            {isLoading ? (
              <div className="text-center py-12">
                <p>Loading apps...</p>
              </div>
            ) : (
              <>
                {featuredTools.length > 0 && (
                  <>
                    <h2 className="text-2xl font-semibold mt-8 lg:mt-0 mb-4">Featured Tools</h2>
                    <div className="space-y-2 bg-white rounded-lg border">
                      {featuredTools.map((tool) => (
                        <ToolCard key={tool.id} tool={tool} />
                      ))}
                    </div>
                  </>
                )}
                
                <h2 className="text-2xl font-semibold mt-8 mb-4">All Tools</h2>
                <div className="space-y-2 bg-white rounded-lg border">
                  {regularTools.map((tool) => (
                    <ToolCard key={tool.id} tool={tool} />
                  ))}
                </div>
                
                {filteredTools.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No tools found matching your criteria.</p>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AllTools;
